import * as THREE from 'three';

import { SkyState } from '../sky/Daylight';

/**
 * Ogniska: ciepłe światło z zamieszkanych budynków po zmroku.
 *
 * # Pula, nie światło na budynek
 *
 * **Liczba świateł jedzie w skompilowanym shaderze.** Three wpisuje ją do
 * programu każdego materiału, więc jej zmiana unieważnia programy i przy
 * najbliższej klatce przekompilowuje **całą scenę** — czyli zacięcie za każdym
 * razem, gdy do kadru wjeżdża albo z niego wyjeżdża chata. Światło na budynek
 * jest więc nie tyle drogie, co niewykonalne: dwadzieścia chat w kadrze to
 * dwadzieścia świateł liczonych na każdym z półtora miliona pikseli bufora
 * i stutter przy każdym przesunięciu kamery.
 *
 * Pula jest stała. Świeci tyle samo świateł, ile zawsze — te, których nikt nie
 * używa, mają zerową siłę — więc liczba w shaderze nie zmienia się nigdy,
 * a koszt jest znany z góry i nie zależy od tego, ile świat ma budynków.
 *
 * # Ciepło, nie jasność
 *
 * Nasza noc **nie jest ciemna** i to jest zapisana decyzja: północ ma 62%
 * światła południa, bo ciemność w małym buforze skalowanym w górę nie jest
 * klimatem, tylko brakiem informacji (patrz „Doba" w `renderer.md`). Ognisko
 * nie wygra więc jasnością — wygrywa **barwą**: pomarańczowa plama na chłodnym,
 * księżycowym gruncie widać od razu, a ta sama plama zrobiona „jaśniej"
 * rozmyłaby się w tym, co i tak świeci.
 *
 * # Czego tu nie ma
 *
 * **Cienia.** Cień z punktu to sześć map głębi na światło — czyli sześć
 * przebiegów sceny na klatkę **razy cała pula**, za coś, co ma być ciepłą plamą
 * pod chatą.
 *
 * **Ognia.** Świeci światło, nie widać płomienia: to jest efekt na terenie
 * i na bryle, a nie rzecz stojąca na polu.
 */

/**
 * Ile świateł naraz.
 *
 * Cena jest liniowa i płacona na każdym pikselu, więc to jest **liczba do
 * zmierzenia, a nie do gustu**: ile chat mieści się w kadrze przy przybliżeniu,
 * na którym patrzy się na własną osadę. Sześć było pierwszym strzałem, dzisiejsza
 * wartość stoi niżej — i stoi **tutaj jedna**, bo dokumentacja wskazuje to
 * miejsce, zamiast powtarzać liczbę, która rozjechałaby się przy pierwszym
 * przestawieniu.
 *
 * Zmiana tej liczby nie jest darmowa w żadną stronę: jedzie ona w skompilowanym
 * shaderze, więc przestawiona **w locie** przekompilowałaby materiały całej
 * sceny. Stąd pula stała, ustalana raz przy starcie.
 */
const POOL = 10;

/**
 * Rodzaje, które palą ogień. Pole uprawne nie — zboże nie ma czym palić.
 *
 * **Jedna lista na ogień i na dym.** Dym nad osadą (`Smoke`) pyta dokładnie tej
 * samej — dwie znaczyłyby budynek, który świeci i nie dymi, przy pierwszej
 * dołożonej chacie.
 */
export const LIT = new Set(['camp', 'lumberjack', 'fishery', 'quarry', 'hunter']);

/** Barwa ognia. */
const FLAME = new THREE.Color(0xff9a3c);

/**
 * Jak wysoko nad ziemią siedzi ogień **bryły bez ustawionego ogniska**.
 *
 * Odwrót, nie reguła: model, któremu ustawiono punkt w panelu, świeci z niego.
 * Ta liczba została z czasów, gdy nie było gdzie zapisać, że ognisko chaty
 * leży pół pola od jej środka — i obowiązuje dalej wszystkiemu, co stoi
 * zastępczą sylwetką z kodu, bo tam nie ma pliku, na którym dałoby się
 * postawić marker.
 */
const HEIGHT = 0.18;

/**
 * Siła i zasięg jednego ogniska.
 *
 * **Zmierzone okiem, nie rachunkiem**, bo nie da się inaczej: światła są u nas
 * fizyczne (three liczy spadek z kwadratem odległości), a nad tym stoi jeszcze
 * mapowanie tonalne ACES — liczba nie ma więc intuicyjnego sensu i jedyne, co
 * o niej wiadomo, to że ma dać ciepłą plamę na jakieś trzy pola, a nie
 * reflektor. Pierwszy strzał, `2.5`, wypalał środek plamy do białego; `1.9`
 * zostawia w niej pomarańcz, a o to w tym świetle chodzi — wygrywa ciepłem,
 * nie jasnością.
 *
 * Zasięg zostaje: przyciemnia się ognisko, a nie zmniejsza krąg, który oświetla.
 */
const BASE = 1.1;
const REACH = 2.5;

/** Jak szybko światło narasta i gaśnie przy przesiadce na inny budynek. */
const EASE = 2.5;

/** Jak mocno i jak szybko migocze. */
const FLICKER = 0.14;
const FLICKER_SPEED = 6.5;

/** Jeden budynek, jak widzi go ta warstwa. */
export interface Hearth {
  id: string;
  key: string;
  /** Ziemia pod budynkiem — ogień pali się przy nim, nie na dachu. */
  position: THREE.Vector3;
  /**
   * Gdzie na tej bryle pali się ogień, w świecie.
   *
   * **Świeci pierwszy, a nie wszystkie**, i to nie jest uproszczenie do
   * poprawienia: pula świateł jest stała na całą scenę, bo jej rozmiar jedzie
   * w skompilowanym shaderze (patrz nagłówek). Drugie ognisko tej samej chaty
   * zabrałoby miejsce sąsiedniej osadzie — czyli zgasiłoby dom, żeby ten sam
   * dach świecił dwa razy. Kominów dymiących jest tyle, ile ustawiono; ogień
   * jest jeden, bo to jest jedno palenisko widziane z daleka.
   *
   * Puste, gdy modelowi nikt tego nie ustawił — wtedy obowiązuje `HEIGHT`.
   */
  hearth: THREE.Vector3[];
  /** Plac budowy. Ognia nie ma: tam się jeszcze nie mieszka. */
  site: boolean;
}

interface Lamp {
  light: THREE.PointLight;
  /** Który budynek trzyma, albo nic. */
  building?: string;
  /** Ile go świeci, 0–1 — po to, żeby przesiadka nie była przeskokiem. */
  level: number;
  /** Własna faza migotania, żeby dwa ogniska obok siebie nie mrugały zgodnie. */
  phase: number;
}

export class Hearths {
  readonly object = new THREE.Group();
  private readonly lamps: Lamp[] = [];
  private time = 0;

  constructor() {
    this.object.name = 'hearths';

    for (let at = 0; at < POOL; at += 1) {
      // `decay` fizyczny i `distance` jako odcięcie: bez tego drugiego światło
      // liczy się na każdej powierzchni w scenie, także po drugiej stronie
      // wzgórza, gdzie i tak nic nie wnosi.
      const light = new THREE.PointLight(FLAME, 0, REACH, 2);
      light.castShadow = false;
      this.lamps.push({ light, level: 0, phase: Math.random() });
      this.object.add(light);
    }
  }

  /**
   * Jedna klatka ognisk.
   *
   * **`standing` jest funkcją, a nie listą, i to nie jest ozdoba.** Spis tego,
   * co stoi w polu, kosztuje obiekt i wektor na budynek — a w dzień nie jest do
   * niczego potrzebny. Wołany dopiero za progiem zmierzchu nie produkuje przez
   * całe południe kilkuset obiektów na klatkę dla pętli, która i tak wychodzi
   * pierwszą linijką.
   *
   * Filtr rodzaju i wybór najbliższych należą tutaj, bo to jest reguła o ogniu,
   * a nie o budynkach.
   */
  update(
    standing: () => Hearth[],
    sky: SkyState,
    focus: THREE.Vector3,
    deltaTime: number
  ): void {
    this.time += deltaTime;

    // **Ogień rozpala się tam, gdzie gasną cienie.** Dwie liczby, które już
    // w projekcie są: `nightness` mówi, ile jest nocy, a `1 - shadowStrength`
    // łapie zmierzch i świt — słońce poniżej 12°, kiedy cień znika, a księżyc
    // jeszcze nisko. Sama noc zapalałaby ognisko dopiero po zachodzie, sam
    // cień gasiłby je o północy, gdy księżyc stoi wysoko.
    const night = Math.max(sky.nightness, 1 - sky.shadowStrength);

    // W dzień nie ma czego liczyć: światła zostają w scenie z zerową siłą, więc
    // liczba w shaderze się nie zmienia, a kosztu żadnego nie ma.
    if (night <= 0) {
      for (const lamp of this.lamps) {
        lamp.level = 0;
        lamp.building = undefined;
        lamp.light.intensity = 0;
      }
      return;
    }

    // Najbliższe punktowi skupienia, bo to jest to, na co gracz patrzy.
    // Kwadrat odległości — pierwiastek na posortowaniu nie zmienia kolejności.
    const near = standing()
      .filter((hearth) => !hearth.site && LIT.has(hearth.key))
      .sort(
        (a, b) => a.position.distanceToSquared(focus) - b.position.distanceToSquared(focus)
      );

    const wanted = near.slice(0, POOL);
    const held = new Set<string>();
    for (const lamp of this.lamps) if (lamp.building) held.add(lamp.building);

    // **Lampa gaśnie, zanim się przesiądzie.** Przestawiona w tej samej klatce,
    // w której zwolniło ją jedno ognisko, przeskoczyłaby przez pół ekranu jako
    // zapalone światło — a to widać znacznie bardziej niż samą przesiadkę.
    const keep = new Set(wanted.map((hearth) => hearth.id));
    for (const lamp of this.lamps) {
      if (lamp.building && !keep.has(lamp.building)) {
        lamp.level = Math.max(0, lamp.level - EASE * deltaTime);
        if (lamp.level <= 0.01) {
          held.delete(lamp.building);
          lamp.building = undefined;
        }
      }
    }

    const free = this.lamps.filter((lamp) => !lamp.building && lamp.level <= 0.01);
    for (const hearth of wanted) {
      if (held.has(hearth.id)) continue;
      const lamp = free.pop();
      if (!lamp) break;
      lamp.building = hearth.id;
      lamp.level = 0;
      held.add(hearth.id);
    }

    const byId = new Map(near.map((hearth) => [hearth.id, hearth]));

    for (const lamp of this.lamps) {
      if (!lamp.building) {
        lamp.light.intensity = 0;
        continue;
      }

      // **Tylko lampa, która swój budynek zatrzymuje, rozjaśnia się i idzie za
      // nim.** Gasnąca ma go jeszcze w ręku — zwalnia go dopiero na zerze —
      // a rozjaśnianie jej tutaj cofałoby to, co przed chwilą zrobiło gaśnięcie.
      // Miejsca też nie zmienia: światło przeskakujące w połowie gaśnięcia jest
      // dokładnie tym przeskokiem, przed którym gaśnięcie broni.
      const hearth = byId.get(lamp.building);
      if (hearth && keep.has(lamp.building)) {
        const spot = hearth.hearth[0];
        if (spot) {
          lamp.light.position.copy(spot);
        } else {
          lamp.light.position.copy(hearth.position);
          lamp.light.position.y += HEIGHT;
        }
        lamp.level = Math.min(1, lamp.level + EASE * deltaTime);
      }

      // Migotanie z dwóch niewspółmiernych sinusów: jeden daje puls, dwa dają
      // coś, w czym nie słychać rytmu.
      const flicker =
        1 +
        FLICKER *
          (Math.sin(this.time * FLICKER_SPEED + lamp.phase * 6.283) * 0.6 +
            Math.sin(this.time * FLICKER_SPEED * 0.41 + lamp.phase * 11.0) * 0.4);

      lamp.light.intensity = BASE * night * lamp.level * flicker;
    }
  }

  dispose(): void {
    for (const lamp of this.lamps) lamp.light.dispose();
    this.object.clear();
  }
}
